/**
 * Publish Review Dialog
 *
 * A comprehensive pre-publish review dialog that shows admins exactly what will change,
 * validates minimum requirements, and provides safety checks before publishing.
 *
 * Features:
 * - Step-by-step publish flow (Review → Validate → Confirm)
 * - Detailed breakdown of banner and category changes
 * - Safety checks with visual feedback (minimum 4 carousel icons, 4 product scrollers, 6 shop categories)
 * - Store-specific validation warnings
 * - Change type indicators (added, removed, modified)
 *
 * @module HomeLayoutManager/components
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Alert,
    AlertTitle,
    CircularProgress,
    TextField,
    Divider,
    Tooltip,
    IconButton,
    Collapse,
    Grid,
} from '@mui/material';
import {
    Publish as PublishIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Image as ImageIcon,
    Category as CategoryIcon,
    Store as StoreIcon,
    ArrowForward as ArrowForwardIcon,
    Visibility as VisibilityIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import type { ChangesResponse, ChangeItem, PropertyChange, CategoryDraft, BannerItemDraft, BrandPosition, DragOperation } from '../types';

// =============================================================================
// Types
// =============================================================================

interface PublishReviewDialogProps {
    /** Whether dialog is open */
    open: boolean;
    /** Close callback */
    onClose: () => void;
    /** Confirm publish callback with notes */
    onConfirm: (notes: string) => void;
    /** Whether publish is in progress */
    isPublishing: boolean;
    /** Changes data from the backend */
    changes: ChangesResponse | null;
    /** Current draft categories for validation */
    categories: CategoryDraft[] | null;
    /** Current banner items for validation */
    bannerItems: BannerItemDraft[] | null;
    /** Current brand positions for validation */
    brandPositions: BrandPosition[] | null;
    /** Pending drag operations for descriptive change display */
    pendingOperations?: DragOperation[];
    /** Meaningful change count (operations + property changes) */
    meaningfulChangeCount?: number;
}

interface ValidationResult {
    isValid: boolean;
    checks: {
        id: string;
        name: string;
        passed: boolean;
        message: string;
        severity: 'error' | 'warning' | 'info';
        count?: number;
        minRequired?: number;
    }[];
}

// =============================================================================
// Constants
// =============================================================================

const MINIMUM_REQUIREMENTS = {
    carouselIcons: 4,
    productScrollers: 4,
    shopCategories: 6,
    homeBrands: 4,
};

const STEPS = ['Review Changes', 'Validate Requirements', 'Confirm Publish'];

// =============================================================================
// Helper Components
// =============================================================================

/**
 * Change type icon with appropriate color
 */
function ChangeTypeIcon({ type }: { type: 'added' | 'removed' | 'modified' }): React.ReactElement {
    switch (type) {
        case 'added':
            return <AddIcon color="success" />;
        case 'removed':
            return <RemoveIcon color="error" />;
        case 'modified':
        default:
            return <EditIcon color="warning" />;
    }
}

/**
 * Change type label
 */
function getChangeTypeLabel(type: 'added' | 'removed' | 'modified'): string {
    switch (type) {
        case 'added':
            return 'Added';
        case 'removed':
            return 'Removed';
        case 'modified':
        default:
            return 'Modified';
    }
}

/**
 * Format a section name for display
 */
function getSectionLabel(section: string): string {
    switch (section) {
        case 'home':
            return 'Home Scrollers';
        case 'carousel':
            return 'Carousel';
        case 'shop':
            return 'Shop';
        case 'brands':
            return 'Brands';
        default:
            return section;
    }
}

/**
 * Human-readable labels for category property fields
 */
const FIELD_LABELS: Record<string, string> = {
    isActive: 'Active',
    showOnHomePage: 'Show on Home',
    showInCarousel: 'Show in Carousel',
    showInCarouselWeb: 'Show in Web Carousel',
    showInShop: 'Show in Shop',
    showTagFilters: 'Tag Filters',
    respectsEarlyCutoff: 'Early Cutoff',
    homeFilterType: 'Filter Type',
    homeFilterValue: 'Filter Value',
};

/**
 * Format a property value for display
 */
function formatPropertyValue(value: unknown): string {
    if (value === null || value === undefined) return 'None';
    if (typeof value === 'boolean') return value ? 'On' : 'Off';
    if (Array.isArray(value)) return value.length === 0 ? 'None' : `${value.length} stores`;
    return String(value);
}

/**
 * Validation check item component
 */
interface ValidationCheckItemProps {
    check: ValidationResult['checks'][0];
    onExpand: () => void;
    expanded: boolean;
}

function ValidationCheckItem({ check, onExpand, expanded }: ValidationCheckItemProps): React.ReactElement {
    const severityColor = {
        error: 'error',
        warning: 'warning',
        info: 'info',
    }[check.severity];

    const severityIcon = {
        error: <ErrorIcon color="error" />,
        warning: <WarningIcon color="warning" />,
        info: <CheckCircleIcon color="success" />,
    }[check.severity];

    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 1,
                borderLeft: 4,
                borderLeftColor: `${severityColor}.main`,
                overflow: 'hidden',
            }}
        >
            <ListItem
                sx={{
                    cursor: check.count !== undefined ? 'pointer' : 'default',
                    bgcolor: check.severity === 'error' ? 'error.lighter' : 'background.paper',
                }}
                onClick={check.count !== undefined ? onExpand : undefined}
            >
                <ListItemIcon>{severityIcon}</ListItemIcon>
                <ListItemText
                    primary={check.name}
                    secondary={check.message}
                    primaryTypographyProps={{ fontWeight: 600 }}
                />
                {check.count !== undefined && check.minRequired !== undefined && (
                    <Chip
                        label={`${check.count}/${check.minRequired}`}
                        color={check.passed ? 'success' : check.severity === 'error' ? 'error' : 'warning'}
                        size="small"
                        sx={{ mr: 1 }}
                    />
                )}
                {check.count !== undefined && (
                    <IconButton size="small">
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
            </ListItem>

            {expanded && check.count !== undefined && (
                <Collapse in={expanded}>
                    <Divider />
                    <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="body2" color="text.secondary">
                            {check.passed
                                ? 'This requirement is satisfied. Good job!'
                                : `You need at least ${check.minRequired} items. Currently have ${check.count}.`}
                        </Typography>
                    </Box>
                </Collapse>
            )}
        </Paper>
    );
}

// =============================================================================
// Main Component
// =============================================================================

export default function PublishReviewDialog({
    open,
    onClose,
    onConfirm,
    isPublishing,
    changes,
    categories,
    bannerItems,
    brandPositions,
    pendingOperations = [],
    meaningfulChangeCount,
}: PublishReviewDialogProps): React.ReactElement {
    const [activeStep, setActiveStep] = useState(0);
    const [notes, setNotes] = useState('');
    const [expandedChecks, setExpandedChecks] = useState<Set<string>>(new Set());
    const [expandedCategories, setExpandedCategories] = useState(true);
    const [expandedBanners, setExpandedBanners] = useState(true);
    const [expandedBrands, setExpandedBrands] = useState(true);
    const [expandedPositionChanges, setExpandedPositionChanges] = useState(true);

    // Reset state when dialog opens
    React.useEffect(() => {
        if (open) {
            setActiveStep(0);
            setNotes('');
            setExpandedChecks(new Set());
            setExpandedCategories(true);
            setExpandedBanners(true);
            setExpandedBrands(true);
            setExpandedPositionChanges(true);
        }
    }, [open]);

    // Calculate validation results
    const validation = useMemo((): ValidationResult => {
        if (!categories) {
            return { isValid: false, checks: [] };
        }

        const checks: ValidationResult['checks'] = [];

        // Check 1: Carousel icons (minimum 4)
        const carouselCategories = categories.filter(
            (c) => c.showInCarousel && c.isActive
        );
        const carouselCount = carouselCategories.length;
        checks.push({
            id: 'carousel',
            name: 'Home Carousel Icons',
            passed: carouselCount >= MINIMUM_REQUIREMENTS.carouselIcons,
            message:
                carouselCount >= MINIMUM_REQUIREMENTS.carouselIcons
                    ? `${carouselCount} icons visible in carousel`
                    : `Need at least ${MINIMUM_REQUIREMENTS.carouselIcons} carousel icons. Currently have ${carouselCount}.`,
            severity: carouselCount >= MINIMUM_REQUIREMENTS.carouselIcons ? 'info' : 'error',
            count: carouselCount,
            minRequired: MINIMUM_REQUIREMENTS.carouselIcons,
        });

        // Check 2: Product scrollers (minimum 4)
        const scrollerCategories = categories.filter(
            (c) => c.showOnHomePage && c.isActive && c.homeFilterType !== 'newOrPopular'
        );
        const scrollerCount = scrollerCategories.length;
        checks.push({
            id: 'scrollers',
            name: 'Home Product Scrollers',
            passed: scrollerCount >= MINIMUM_REQUIREMENTS.productScrollers,
            message:
                scrollerCount >= MINIMUM_REQUIREMENTS.productScrollers
                    ? `${scrollerCount} product scrollers on home page`
                    : `Need at least ${MINIMUM_REQUIREMENTS.productScrollers} product scrollers. Currently have ${scrollerCount}.`,
            severity: scrollerCount >= MINIMUM_REQUIREMENTS.productScrollers ? 'info' : 'error',
            count: scrollerCount,
            minRequired: MINIMUM_REQUIREMENTS.productScrollers,
        });

        // Check 3: Shop categories (minimum 6)
        const shopCategories = categories.filter((c) => c.showInShop && c.isActive);
        const shopCount = shopCategories.length;
        checks.push({
            id: 'shop',
            name: 'Shop Page Categories',
            passed: shopCount >= MINIMUM_REQUIREMENTS.shopCategories,
            message:
                shopCount >= MINIMUM_REQUIREMENTS.shopCategories
                    ? `${shopCount} categories in shop grid`
                    : `Need at least ${MINIMUM_REQUIREMENTS.shopCategories} shop categories. Currently have ${shopCount}.`,
            severity: shopCount >= MINIMUM_REQUIREMENTS.shopCategories ? 'info' : 'error',
            count: shopCount,
            minRequired: MINIMUM_REQUIREMENTS.shopCategories,
        });

        // Check 4: Active categories warning
        const activeCategories = categories.filter((c) => c.isActive);
        if (activeCategories.length === 0) {
            checks.push({
                id: 'active',
                name: 'Active Categories',
                passed: false,
                message: 'No active categories! Users will see an empty shop.',
                severity: 'error',
            });
        }

        // (No store-specific check: one layout for every store.)

        // Check 6: Home brands (minimum 4)
        const brandCount = brandPositions?.length ?? 0;
        checks.push({
            id: 'brands',
            name: 'Home Brand Scroller',
            passed: brandCount >= MINIMUM_REQUIREMENTS.homeBrands,
            message:
                brandCount >= MINIMUM_REQUIREMENTS.homeBrands
                    ? `${brandCount} brands in home scroller`
                    : `Need at least ${MINIMUM_REQUIREMENTS.homeBrands} brands. Currently have ${brandCount}.`,
            severity: brandCount >= MINIMUM_REQUIREMENTS.homeBrands ? 'info' : 'warning',
            count: brandCount,
            minRequired: MINIMUM_REQUIREMENTS.homeBrands,
        });

        const isValid = checks.every((c) => c.severity !== 'error');

        return { isValid, checks };
    }, [categories, brandPositions]);

    // Toggle expanded check
    const toggleCheckExpanded = useCallback((checkId: string) => {
        setExpandedChecks((prev) => {
            const next = new Set(prev);
            if (next.has(checkId)) {
                next.delete(checkId);
            } else {
                next.add(checkId);
            }
            return next;
        });
    }, []);

    // Handle next step
    const handleNext = useCallback(() => {
        if (activeStep === 1 && !validation.isValid) {
            // Don't proceed if validation fails
            return;
        }
        setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }, [activeStep, validation.isValid]);

    // Handle back step
    const handleBack = useCallback(() => {
        setActiveStep((prev) => Math.max(prev - 1, 0));
    }, []);

    // Handle confirm
    const handleConfirm = useCallback(() => {
        onConfirm(notes);
    }, [notes, onConfirm]);

    // Calculate change counts
    const totalChanges = changes?.totalChanges ?? 0;
    const displayChangeCount = meaningfulChangeCount ?? totalChanges;
    const bannerChanges = changes?.banners ?? [];
    const categoryChanges = changes?.categories ?? [];
    const brandChanges = changes?.brands ?? [];

    // Separate property-only changes (exclude items already tracked as position operations)
    const operationItemIds = useMemo(
        () => new Set(pendingOperations.map((op) => op.itemId)),
        [pendingOperations]
    );

    const propertyOnlyBannerChanges = useMemo(
        () => bannerChanges.filter((c) => !operationItemIds.has(c.id) && c.changeCategory !== 'position'),
        [bannerChanges, operationItemIds]
    );

    const propertyOnlyCategoryChanges = useMemo(
        () => categoryChanges.filter((c) => !operationItemIds.has(c.id) && c.changeCategory !== 'position'),
        [categoryChanges, operationItemIds]
    );

    const propertyOnlyBrandChanges = useMemo(
        () => brandChanges.filter((c) => !operationItemIds.has(c.id) && c.changeCategory !== 'position'),
        [brandChanges, operationItemIds]
    );

    const hasPropertyChanges = propertyOnlyBannerChanges.length + propertyOnlyCategoryChanges.length + propertyOnlyBrandChanges.length > 0;

    // Render step content
    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <AlertTitle>Review Your Changes</AlertTitle>
                            You're about to publish <strong>{displayChangeCount} change
                                {displayChangeCount === 1 ? '' : 's'}</strong> to the live home page.
                            These changes will be visible to all app users immediately.
                        </Alert>

                        {/* Position Changes (from tracked drag operations) */}
                        {pendingOperations.length > 0 && (
                            <Paper variant="outlined" sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'info.lighter',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExpandedPositionChanges(!expandedPositionChanges)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ArrowForwardIcon color="info" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Position Changes
                                        </Typography>
                                        <Chip
                                            label={`${pendingOperations.length} moved`}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <IconButton size="small">
                                        {expandedPositionChanges ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                                <Collapse in={expandedPositionChanges}>
                                    <Divider />
                                    <List dense>
                                        {pendingOperations.map((op) => (
                                            <ListItem key={`${op.itemId}-${op.section}`}>
                                                <ListItemIcon>
                                                    <ArrowForwardIcon color="info" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={op.itemName}
                                                    secondary={`Moved from #${op.fromPosition} to #${op.toPosition}`}
                                                />
                                                <Chip
                                                    label={getSectionLabel(op.section)}
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </Paper>
                        )}

                        {/* Property Changes — only items not already tracked as position operations */}
                        {/* Banner Property Changes */}
                        {propertyOnlyBannerChanges.length > 0 && (
                            <Paper variant="outlined" sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'primary.lighter',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExpandedBanners(!expandedBanners)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ImageIcon color="primary" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Banner Changes
                                        </Typography>
                                        <Chip
                                            label={`${propertyOnlyBannerChanges.length} modified`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <IconButton size="small">
                                        {expandedBanners ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                                <Collapse in={expandedBanners}>
                                    <Divider />
                                    <List dense>
                                        {propertyOnlyBannerChanges.map((change) => (
                                            <ListItem key={change.id}>
                                                <ListItemIcon>
                                                    <ChangeTypeIcon type={change.type} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={change.name}
                                                    secondary={getChangeTypeLabel(change.type)}
                                                />
                                                <Chip
                                                    label={change.type}
                                                    size="small"
                                                    color={
                                                        change.type === 'added'
                                                            ? 'success'
                                                            : change.type === 'removed'
                                                                ? 'error'
                                                                : 'warning'
                                                    }
                                                    variant="outlined"
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </Paper>
                        )}

                        {/* Category Property Changes */}
                        {propertyOnlyCategoryChanges.length > 0 && (
                            <Paper variant="outlined" sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'success.lighter',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExpandedCategories(!expandedCategories)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CategoryIcon color="success" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Category Changes
                                        </Typography>
                                        <Chip
                                            label={`${propertyOnlyCategoryChanges.length} modified`}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <IconButton size="small">
                                        {expandedCategories ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                                <Collapse in={expandedCategories}>
                                    <Divider />
                                    <List dense>
                                        {propertyOnlyCategoryChanges.map((change) => (
                                            <React.Fragment key={change.id}>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <ChangeTypeIcon type={change.type} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={change.name}
                                                        secondary={
                                                            change.propertyChanges && change.propertyChanges.length > 0
                                                                ? change.propertyChanges.map((pc) =>
                                                                    `${FIELD_LABELS[pc.field] || pc.field}: ${formatPropertyValue(pc.from)} → ${formatPropertyValue(pc.to)}`
                                                                ).join(', ')
                                                                : getChangeTypeLabel(change.type)
                                                        }
                                                    />
                                                    <Chip
                                                        label={
                                                            change.propertyChanges && change.propertyChanges.length > 0
                                                                ? `${change.propertyChanges.length} field${change.propertyChanges.length === 1 ? '' : 's'}`
                                                                : change.type
                                                        }
                                                        size="small"
                                                        color={
                                                            change.type === 'added'
                                                                ? 'success'
                                                                : change.type === 'removed'
                                                                    ? 'error'
                                                                    : 'warning'
                                                        }
                                                        variant="outlined"
                                                    />
                                                </ListItem>
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Collapse>
                            </Paper>
                        )}

                        {/* Brand Property Changes */}
                        {propertyOnlyBrandChanges.length > 0 && (
                            <Paper variant="outlined" sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'warning.lighter',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExpandedBrands(!expandedBrands)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <StoreIcon color="warning" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Brand Changes
                                        </Typography>
                                        <Chip
                                            label={`${propertyOnlyBrandChanges.length} modified`}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <IconButton size="small">
                                        {expandedBrands ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                                <Collapse in={expandedBrands}>
                                    <Divider />
                                    <List dense>
                                        {propertyOnlyBrandChanges.map((change) => (
                                            <ListItem key={change.id}>
                                                <ListItemIcon>
                                                    <ChangeTypeIcon type={change.type} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={change.name}
                                                    secondary={getChangeTypeLabel(change.type)}
                                                />
                                                <Chip
                                                    label={change.type}
                                                    size="small"
                                                    color={
                                                        change.type === 'added'
                                                            ? 'success'
                                                            : change.type === 'removed'
                                                                ? 'error'
                                                                : 'warning'
                                                    }
                                                    variant="outlined"
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </Paper>
                        )}

                        {displayChangeCount === 0 && (
                            <Alert severity="warning">
                                <AlertTitle>No Changes Detected</AlertTitle>
                                There are no unpublished changes to publish. Make some changes
                                first before publishing.
                            </Alert>
                        )}
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Alert
                            severity={validation.isValid ? 'success' : 'error'}
                            sx={{ mb: 3 }}
                        >
                            <AlertTitle>
                                {validation.isValid
                                    ? 'All Requirements Met'
                                    : 'Requirements Not Met'}
                            </AlertTitle>
                            {validation.isValid
                                ? 'Your layout meets all minimum requirements. You can proceed to publish.'
                                : 'Please fix the errors below before publishing. These requirements ensure users have a good experience.'}
                        </Alert>

                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                            Minimum Requirements Check
                        </Typography>

                        {validation.checks.map((check) => (
                            <ValidationCheckItem
                                key={check.id}
                                check={check}
                                onExpand={() => toggleCheckExpanded(check.id)}
                                expanded={expandedChecks.has(check.id)}
                            />
                        ))}

                        <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'info.lighter' }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Why these requirements?</strong>
                                <br />
                                • <strong>4+ Carousel Icons:</strong> Users need enough categories to browse comfortably
                                <br />
                                • <strong>4+ Product Scrollers:</strong> Home page should have engaging content sections
                                <br />
                                • <strong>6+ Shop Categories:</strong> Shop page needs variety for good discovery
                                <br />
                                • <strong>4+ Home Brands:</strong> Brand scroller should have enough brands visible
                            </Typography>
                        </Paper>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <AlertTitle>Final Confirmation</AlertTitle>
                            You are about to publish <strong>{displayChangeCount} change
                                {displayChangeCount === 1 ? '' : 's'}</strong> to the live home page.
                            This action cannot be undone.
                        </Alert>

                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Summary
                            </Typography>
                            <Grid container spacing={2}>
                                {pendingOperations.length > 0 && (
                                    <Grid size={{ xs: 4 }}>
                                        <Box sx={{ textAlign: 'center', p: 1 }}>
                                            <Typography variant="h4" color="info.main">
                                                {pendingOperations.length}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Position Changes
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {propertyOnlyBannerChanges.length > 0 && (
                                    <Grid size={{ xs: pendingOperations.length > 0 ? 4 : 4 }}>
                                        <Box sx={{ textAlign: 'center', p: 1 }}>
                                            <Typography variant="h4" color="primary">
                                                {propertyOnlyBannerChanges.length}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Banner Changes
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {propertyOnlyCategoryChanges.length > 0 && (
                                    <Grid size={{ xs: 4 }}>
                                        <Box sx={{ textAlign: 'center', p: 1 }}>
                                            <Typography variant="h4" color="success.main">
                                                {propertyOnlyCategoryChanges.length}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Category Changes
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {propertyOnlyBrandChanges.length > 0 && (
                                    <Grid size={{ xs: 4 }}>
                                        <Box sx={{ textAlign: 'center', p: 1 }}>
                                            <Typography variant="h4" color="warning.main">
                                                {propertyOnlyBrandChanges.length}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Brand Changes
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        <TextField
                            label="Publish Notes (Optional)"
                            placeholder="e.g., Updated holiday banners, added new categories"
                            fullWidth
                            multiline
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={isPublishing}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="caption" color="text.secondary">
                            These notes will be saved in the publish history for future reference.
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isPublishing ? undefined : onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '60vh', maxHeight: '80vh' },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublishIcon color="success" />
                    <Typography variant="h6">Publish Changes</Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderStepContent()}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                {activeStep > 0 && (
                    <Button onClick={handleBack} disabled={isPublishing}>
                        Back
                    </Button>
                )}

                <Box sx={{ flex: 1 }} />

                <Button onClick={onClose} disabled={isPublishing} color="inherit">
                    Cancel
                </Button>

                {activeStep < STEPS.length - 1 ? (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={activeStep === 1 && !validation.isValid}
                        endIcon={<ArrowForwardIcon />}
                    >
                        Next
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleConfirm}
                        disabled={isPublishing || displayChangeCount === 0}
                        startIcon={
                            isPublishing ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <PublishIcon />
                            )
                        }
                        sx={{
                            background: 'linear-gradient(135deg, #10B981, #059669)',
                            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.25)',
                        }}
                    >
                        {isPublishing ? 'Publishing...' : 'Publish Live'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
